import { createContext, useContext, useEffect, useState, useCallback } from "react";
import toast from "react-hot-toast";

import { queryClient } from "../lib/queryClient";
import * as authApi from "../api/auth";

// ADMIN_PANEL_ARCHITECTURE_TEMPLATE.md §6 (auth context) + §7 (idle auto-logout), followed exactly.

const AuthContext = createContext(null);

// §7 — an admin panel left open on an unattended machine is the most common real-world breach path
// for internal tools. The ERP's own session is a fixed 24 hours with no refresh
// (technical-foundation §14.1), so an idle logout is the only thing shortening that window.
const IDLE_LIMIT_MS = 30 * 60 * 1000;
const IDLE_KEY = "vvr_admin_last_activity";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // The second half of the `/auth/me` payload. It was being fetched and thrown away, so nothing in
  // the panel could react to a forced password change — and `requirePasswordChanged` 403s every
  // business route while it is true, which is every route this panel has.
  const [mustChangePassword, setMustChangePassword] = useState(false);

  // The server, not the client, decides who is logged in. On every boot and full reload we ask.
  // There is no persisted user object to go stale, so a revoked session cannot outlive its revocation.
  const checkAuth = useCallback(async () => {
    try {
      // `GET /auth/me` answers `{ user, mustChangePassword }` — the user is NESTED, not the payload
      // itself (authService.getCurrentUser). Spreading the envelope instead of `.user` left
      // `designation`, `department`, `_id`, `fullName` and `userType` all undefined, which made
      // `isStaff()` false for every account and refused every staff login at ProtectedRoute.
      const { user: me, mustChangePassword: forced } = await authApi.checkAuth();
      // Normalise once, here, at the boundary — so no predicate downstream needs a null check.
      setUser({ ...me, designation: me.designation || null, department: me.department || null });
      setMustChangePassword(Boolean(forced));
    } catch {
      setUser(null); // 401 → not logged in. Not an error state.
      setMustChangePassword(false);
    } finally {
      setLoading(false); // ALWAYS runs — the loading gate in ProtectedRoute depends on it
    }
  }, []);

  useEffect(() => {
    // FALSE POSITIVE, SUPPRESSED DELIBERATELY.
    // `react-hooks` v7 reports "setState called synchronously within an effect" for any effect that
    // calls a function whose body contains a setState. It does not follow the call through the
    // `useCallback` to see that EVERY setState in `checkAuth` sits after `await authApi.checkAuth()`,
    // so none of them runs in the effect's synchronous body and none can cascade a render the way the
    // rule describes. The cascading-render hazard the rule exists to prevent is not present here.
    //
    // Suppressed rather than restructured: the shapes that satisfy the analyser all defer the identity
    // check across an extra microtask, and this effect is the ONLY thing that establishes who is logged
    // in. `loading` starts `true` and `ProtectedRoute` gates on it, so delaying the check to appease a
    // static rule would widen the window before the session is known, in the one place where being
    // wrong logs the wrong person in. The rule is the thing that is inaccurate, not the code.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setUser(null);
      setMustChangePassword(false);
      // Wipe every cached query. Without this, the next account to log in on this browser briefly
      // sees the previous account's cached reports — a real data leak, because cached data is served
      // instantly before any refetch. On a reporting panel the cache holds business figures.
      queryClient.clear();
    }
  }, []);

  // ── §7 · Idle auto-logout ─────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return undefined;

    const markActive = () => {
      try {
        localStorage.setItem(IDLE_KEY, String(Date.now()));
      } catch {
        /* private mode — idle logout degrades to per-tab, which is still correct */
      }
    };

    // Throttle: mousemove fires hundreds of times a second. One write per 5s.
    let lastWrite = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - lastWrite > 5000) {
        lastWrite = now;
        markActive();
      }
    };

    markActive(); // seed on login, so a fresh session isn't instantly idle

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));

    const interval = setInterval(() => {
      let last = 0;
      try {
        last = parseInt(localStorage.getItem(IDLE_KEY) || "0", 10);
      } catch {
        /* ignore */
      }
      if (!last) {
        markActive();
        return;
      }
      if (Date.now() - last >= IDLE_LIMIT_MS) {
        clearInterval(interval);
        toast("Logged out after 30 minutes of inactivity.", { icon: "💤" });
        // A REAL logout: the server clears the session and the query cache is wiped. Setting
        // `user = null` locally would leave a live session on the server — the exact thing this
        // feature exists to prevent.
        logout();
      }
    }, 30 * 1000);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      clearInterval(interval);
    };
    // `localStorage` rather than a ref, so the idle window is ACCOUNT-WIDE: logout fires only when
    // every open tab has been idle for the full 30 minutes.
  }, [user, logout]);

  return (
    <AuthContext.Provider value={{ user, loading, mustChangePassword, logout, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
